import { useEffect, useRef, useState } from 'react'
import { countdownParts, countdownLabel, DAY, SECOND } from './countdownParts'
import styles from './Countdown.module.css'

export type CountdownSize = 'md' | 'lg'

export interface CountdownProps {
  /** When it reaches zero: an ISO string, a Date, or epoch milliseconds. */
  to: string | Date | number
  size?: CountdownSize
  /**
   * Under this much time left the figures take the brand colour — Carousel's
   * gradient, or whatever `--countdown-urgent` is set to. Default 24 hours; 0 never.
   */
  urgentWithin?: number
  /** Shown in place of the units once time is up. */
  expiredText?: string
  /** Fires once, when it reaches zero while on screen. */
  onComplete?: () => void
  className?: string
}

/**
 * A live countdown: days, hours, minutes and seconds, ticking each second.
 * Days drop out once there are none left, so the last day reads as hh:mm:ss
 * rather than leading with a zero.
 */
export function Countdown({
  to,
  size = 'md',
  urgentWithin = DAY,
  expiredText = 'Closed',
  onComplete,
  className,
}: CountdownProps) {
  const target = typeof to === 'number' ? to : new Date(to).getTime()
  const [now, setNow] = useState(() => Date.now())
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])

  const parts = countdownParts(target, now)
  const done = parts.remaining === 0

  useEffect(() => {
    if (target - Date.now() <= 0) return
    // Re-read the clock on every tick instead of counting ticks, so a
    // throttled background tab is right again the moment it is looked at.
    const id = setInterval(() => {
      const t = Date.now()
      setNow(t)
      if (t >= target) clearInterval(id)
    }, SECOND)
    return () => clearInterval(id)
  }, [target])

  // Fires when time runs out on screen — however late the tick that notices
  // it. One that was already over when it mounted never "ran out" here.
  const [wasRunning, setWasRunning] = useState(!done)
  const [fired, setFired] = useState(false)
  if (!done && !wasRunning) setWasRunning(true)
  useEffect(() => {
    if (done && wasRunning && !fired) {
      setFired(true)
      onCompleteRef.current?.()
    }
  }, [done, wasRunning, fired])

  const urgent = !done && urgentWithin > 0 && parts.remaining <= urgentWithin
  const classes = [styles.countdown, styles[size], urgent && styles.urgent, className].filter(Boolean).join(' ')

  if (done) {
    return <span role="timer" className={`${classes} ${styles.expired}`} aria-label={expiredText}>{expiredText}</span>
  }

  const units: [number, string][] = [
    ...(parts.days > 0 ? [[parts.days, 'd'] as [number, string]] : []),
    [parts.hours, 'h'],
    [parts.minutes, 'm'],
    [parts.seconds, 's'],
  ]

  return (
    <span role="timer" aria-live="off" aria-label={countdownLabel(parts)} className={classes}>
      {units.map(([value, unit]) => (
        <span key={unit} className={styles.unit} aria-hidden="true">
          <span className={styles.value}>{unit === 'd' ? value : String(value).padStart(2, '0')}</span>
          <span className={styles.suffix}>{unit}</span>
        </span>
      ))}
    </span>
  )
}
